import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  Users, 
  Plus, 
  Heart,
  MessageSquare,
  Loader2,
  Filter
} from 'lucide-react';

export default function CommunityPage() {
  const { user, token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPostDialog, setShowPostDialog] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', content: '', category: 'general' });
  const [creating, setCreating] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');

  useEffect(() => {
    fetchPosts();
  }, [token, categoryFilter]);

  const fetchPosts = async () => {
    try {
      const params = categoryFilter ? `?category=${categoryFilter}` : '';
      const response = await axios.get(`${API}/forum/posts${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const createPost = async () => {
    if (!newPost.title.trim() || !newPost.content.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setCreating(true);
    try {
      const params = new URLSearchParams({
        title: newPost.title,
        content: newPost.content,
        category: newPost.category
      });
      
      await axios.post(
        `${API}/forum/posts?${params}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Post created!');
      setShowPostDialog(false);
      setNewPost({ title: '', content: '', category: 'general' });
      fetchPosts();
    } catch (error) {
      toast.error('Failed to create post');
    } finally {
      setCreating(false);
    }
  };

  const likePost = async (postId) => {
    try {
      const response = await axios.post(
        `${API}/forum/posts/${postId}/like`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchPosts();
    } catch (error) {
      toast.error('Failed to like post');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'general', label: 'General' },
    { value: 'workouts', label: 'Workouts' },
    { value: 'nutrition', label: 'Nutrition' },
    { value: 'motivation', label: 'Motivation' },
    { value: 'questions', label: 'Questions' }
  ];

  return (
    <div data-testid="community-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase">
            <span className="text-primary">COMMUNITY</span> FORUM
          </h1>
          <p className="text-gray-400 mt-1">Connect, share, and grow together</p>
        </div>
        
        <Dialog open={showPostDialog} onOpenChange={setShowPostDialog}>
          <DialogTrigger asChild>
            <Button 
              data-testid="create-post-btn"
              className="bg-primary hover:bg-red-700 text-white uppercase font-semibold"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Post
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-white uppercase">
                Create Post
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Title</Label>
                <Input
                  data-testid="post-title-input"
                  value={newPost.title}
                  onChange={(e) => setNewPost({...newPost, title: e.target.value})}
                  className="bg-secondary border-transparent"
                  placeholder="What's on your mind?"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Category</Label>
                <Select 
                  value={newPost.category} 
                  onValueChange={(v) => setNewPost({...newPost, category: v})}
                >
                  <SelectTrigger className="bg-secondary border-transparent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="workouts">Workouts</SelectItem>
                    <SelectItem value="nutrition">Nutrition</SelectItem>
                    <SelectItem value="motivation">Motivation</SelectItem>
                    <SelectItem value="questions">Questions</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Content</Label>
                <Textarea
                  data-testid="post-content-input"
                  value={newPost.content}
                  onChange={(e) => setNewPost({...newPost, content: e.target.value})}
                  className="bg-secondary border-transparent min-h-[120px]"
                  placeholder="Share your thoughts, tips, or questions..."
                />
              </div>
              
              <Button 
                data-testid="submit-post-btn"
                onClick={createPost}
                disabled={creating}
                className="w-full bg-primary hover:bg-red-700 text-white uppercase font-semibold"
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Post'
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <Select value={categoryFilter || "all"} onValueChange={(v) => setCategoryFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48 bg-secondary border-transparent">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            {categories.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Posts */}
      {posts.length === 0 ? (
        <div className="text-center py-12 card-tactical">
          <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl text-white mb-2">No posts yet</h3>
          <p className="text-gray-400 mb-4">Be the first to start a conversation!</p>
          <Button 
            onClick={() => setShowPostDialog(true)}
            className="bg-primary hover:bg-red-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create First Post
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <div key={post.id} className="card-tactical">
              <div className="flex items-start gap-4">
                <Avatar>
                  <AvatarFallback className="bg-primary/20 text-primary">
                    {post.author_name?.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white font-medium">{post.author_name}</span>
                    <span className="text-xs text-gray-500">•</span>
                    <span className="text-xs text-gray-500">
                      {new Date(post.created_at).toLocaleDateString()}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-sm capitalize ${
                      post.category === 'motivation' ? 'bg-green-500/20 text-green-500' :
                      post.category === 'workouts' ? 'bg-blue-500/20 text-blue-500' :
                      post.category === 'nutrition' ? 'bg-orange-500/20 text-orange-500' :
                      post.category === 'questions' ? 'bg-purple-500/20 text-purple-500' :
                      'bg-gray-500/20 text-gray-500'
                    }`}>
                      {post.category}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-white mb-2">{post.title}</h3>
                  <p className="text-gray-300 mb-4">{post.content}</p>
                  
                  <div className="flex items-center gap-4">
                    <button 
                      data-testid={`like-post-${post.id}`}
                      onClick={() => likePost(post.id)}
                      className={`flex items-center gap-1 text-sm transition-colors ${
                        post.likes?.includes(user?.id) 
                          ? 'text-primary' 
                          : 'text-gray-400 hover:text-primary'
                      }`}
                    >
                      <Heart className={`w-4 h-4 ${post.likes?.includes(user?.id) ? 'fill-current' : ''}`} />
                      {post.likes?.length || 0}
                    </button>
                    
                    <button className="flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors">
                      <MessageSquare className="w-4 h-4" />
                      {post.comments?.length || 0}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
